import { Grid } from "@material-ui/core";
import { orderBy } from "lodash";
import { memo, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { arrayToObject } from "utils/object-util";
import { permissionProfiles } from "../../../../components/Authorize/permissionProfiles";
import DataTable from "../../../../components/DataTable/DataTable";
import NotificationProvider from "../../../../components/NotificationProvider";
import { userPreferencesComponentIds } from "../../../../components/UserPreferences/userPreferencesConsts";
import usePrompt from "../../../../hooks/usePrompt";
import useSortOptions from "../../../../hooks/useSortOptions";
import { IconEdit } from "../../../../icons";
import FilterService, { fieldTypes } from "../../../../services/filterService";
import { useDeleteUserMutation } from "../../usersSlice";
import EditUserDialog from "../UserDialogs/EditUserDialog";
import useStyles from "./styles";
import { setFilters, setFiltersSetFromOtherScreen, setSearchVal } from "./usersTableFiltersSlice";

const UsersTable = ({
    users,
    permission,
    roles,
    clients,
    lots,
    refetchUsers,
    title,
    showAddBlackList = true
}) => {
    let usersFilter = users || [];
    const userTypes = [{ id: 1, name: "Internal" }, { id: 2, name: "Client" }];
    const rolesFilter = roles || [];
    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(100);

    const classes = useStyles();
    const [sortOptions, sortData] = useSortOptions();
    const { triggerPrompt } = usePrompt();
    const [deleteUser, { isLoading: isDeletingUser }] = useDeleteUserMutation();
    const [clickedUser, setClickedUser] = useState(null);
    const dispatch = useDispatch();
    const clientAccounts = clients?.flatMap(c => c.clientAccounts);

    const { searchVal, filters, filtersSetFromOtherScreen } = useSelector(state => state.usersTableFilters);
    const lotLookup = arrayToObject(lots);
    const clientAccountLookup = arrayToObject(clientAccounts);

    const usersColumns = [
        { name: "Name", key: "name" },
        { name: "User Name", key: "userName" },
        { name: "Email", key: "email" },
        { name: "Assigned Role", key: "assignedRoleDisplay" },
        { name: "Last LogIn", key: "lastLoginDateFormat" },
        { name: "Last Seen", key: "lastSeenDateFormat" },
        {
            name: "Associations",
            key: "userRelations",
            component: (row) => {
                return <div>{row?.userRelations?.map((u, index) => <Grid spacing={1} key={index} container>
                    <Grid item>{u?.relationTypeName}: </Grid>
                    <Grid item>{lotLookup?.[u?.lotId]?.name}</Grid>
                </Grid>)}</div>
            },
            exporKey: "relationsDisplay"
        },
        {
            name: "Client Access",
            key: "accessClient",
            component: (row) => {
                return <div>{row?.accessClient?.map((u, index) => <Grid key={index} container>
                    <Grid item>{clientAccountLookup?.[u?.id]?.name}</Grid>
                </Grid>)}</div>
            },
            exporKey: "clientsDisplay"
        },
        {
            name: "Lot Access",
            key: "accessLot",
            component: (row) => {
                return <div>{row?.accessLot?.map((u, index) => <Grid key={index} container>
                    <Grid item>{lotLookup?.[u?.id]?.name}</Grid>
                </Grid>)}</div>
            },
            exporKey: "lotsDisplay"
        },
    ];

    const userRelations = [
        { id: 1, name: "Attendant" },
        { id: 2, name: "Supervisor" },
        { id: 3, name: "Regional Manager" }
    ];

    const updateUsersWithAssignedRoles = (users, roles) => {
        return users.map(user => {
            const matchingRoles = user.assignedRole
                .map(userRole => roles.find(role =>
                    role.key?.trim()?.toLowerCase() === userRole.roleId?.trim()?.toLowerCase()))
                .filter(role => role)
            const assignedRoleDisplay = matchingRoles.map(role => role.name).join('; ');

            return {
                ...user,
                assignedRoleDisplay,
                assignedRoleIds: user?.assignedRole?.map(item => item.roleId),
                relationTypesTypes: user?.userRelations?.map(item => item.relationType),
                relationLotIds: user?.userRelations?.map(item => item.lotId),
                accessLotIds: user?.accessLot?.map(item => item.id),
                accessClientIds: user?.accessClient?.map(item => item.id),
                assignedRoleId: user?.assignedRole?.roleId,
                permissionKeys: user?.assignedPermission?.map(item => item.permissionKey.trim().toLowerCase()),
                relationsDisplay: user?.userRelations?.map(u => `${u?.relationTypeName}: ${lotLookup?.[u?.lotId]?.name}`),
                clientsDisplay: user?.accessClient?.map(u => `${clientAccountLookup?.[u?.id]?.name}`),
                lotsDisplay: user?.accessLot?.map(u => `${lotLookup?.[u?.id]?.name}`)
            }
        });
    };

    usersFilter = useMemo(() => updateUsersWithAssignedRoles(usersFilter, rolesFilter), [usersFilter, rolesFilter]);

    const filterConfig = [
        {
            type: fieldTypes.VALUE_LIST,
            size: 2,
            title: 'Client Access',
            key: 'accessClientIds',
            values: clientAccounts,
        },
        {
            type: fieldTypes.VALUE_LIST,
            size: 2,
            title: 'Lot Access',
            key: 'accessLotIds',
            values: lots,
        },
        {
            type: fieldTypes.VALUE_LIST,
            size: 2,
            title: 'Association',
            key: 'relationTypesTypes',
            values: userRelations,
        },
        {
            type: fieldTypes.VALUE_LIST,
            size: 2,
            title: 'Associated to Lot',
            key: 'relationLotIds',
            values: lots,
        },
        {
            type: fieldTypes.VALUE_LIST,
            size: 2,
            title: 'Role',
            key: 'assignedRoleIds',
            values: rolesFilter?.map(x => ({ name: x.name, id: x.key })),
        },
        {
            type: fieldTypes.VALUE_LIST,
            size: 2,
            title: 'Has Access To',
            key: 'permissionKeys',
            values: permission?.map(x => ({ name: x.name, id: x.key })),
        },
        {
            type: fieldTypes.LIST,
            size: 2,
            title: 'Type',
            key: 'userType',
            values: userTypes,
        },
    ];

    const onDropFilter = (filterValue, prop) => {
        updateFilters(prop, filterValue);
    };

    const updateFilters = (prop, value) => {
        setPage(1);
        const newFilters = { ...filters, [prop]: { value } }
        dispatch(setFilters(newFilters));
    }
    const searchUserByText = value => {
        setPage(1);
        dispatch(setSearchVal(value.trim()));
    }
    const onResetFilters = () => {
        dispatch(setFilters({
            clientId: { value: null },
            lotId: { value: null }
        }));
    }
    const updateAllFilters = (newFilters) => {
        setPage(1);
        dispatch(setFilters(newFilters));
    }

    const setFiltersSetFromOtherScreenFunc = (val) => {
        dispatch(setFiltersSetFromOtherScreen(val));
    }
    const deleteUserConfirmation = async (user) => {
        if (!user || !user?.id) return { error: "Failed to delete" };
        return await deleteUser({ userId: user?.id });
    };

    const getUserById = (id) => {
        return usersFilter?.find(u => u.id === id);
    };

    const onEdit = (id) => {
        const user = getUserById(id);
        setClickedUser(user);
    };

    const onCancel = () => {
        setClickedUser(null);
    };

    const onDelete = (rowId) => {
        const user = getUserById(rowId);
        triggerPrompt({
            title: "Add to blacklist",
            content: "Are you sure you want to add to blacklist this user?",
            onConfirm: async () => {
                let res = await deleteUserConfirmation(user);
                if (!res?.error) {
                    refetchUsers();
                    NotificationProvider.success('Add to blacklist successfully');
                } else {
                    NotificationProvider.error('Failed to delete');
                }
            },
        });
    };

    const rowActions = (rowId) => {
        const actions = [{
            icon: <IconEdit />,
            label: 'Edit',
            onClick: () => onEdit(rowId),
            profile: permissionProfiles.USER_MANAGEMENT.EDIT_USER
        }];
        // if (showAddBlackList) {
        //     actions.push({
        //         icon: <IconAddCircle width={10} />,
        //         label: 'Add to blacklist',
        //         onClick: () => onDelete(rowId),
        //         profile: permissionProfiles.USER_MANAGEMENT.DELETE_USER
        //     });
        // }
        return actions;
    };


    let filteredUsers = FilterService.filter(filters, searchVal, usersFilter, filterConfig);

    const onChangePagination = async (page, limit) => {
        setPage(page);
        setLimit(limit);
    }

    const mapExportData = v => {
        const info = {};
        usersColumns?.map(col => {
            info[col.name] = v?.[col.exporKey] ?? v?.[col.key]
        })
        return info;
    };


    return (
        <>
            <DataTable
                title={<>{title} ({filteredUsers?.length ?? 0})</>}

                columns={usersColumns}
                rows={orderBy(filteredUsers, sortOptions.columnToSort, sortOptions.sortDirection)}
                rowIdentifier='id'
                onSort={sortData}
                sortDirection={sortOptions.sortDirection}
                columnToSort={sortOptions.columnToSort}

                actions={rowActions}
                showActionsInMenu

                dropDownsConfig={filterConfig}
                filters={filters}
                onDropFilter={onDropFilter}

                onChangePagination={onChangePagination}
                limit={limit}

                withFilterPanel={true}
                onResetFilters={onResetFilters}
                updateAllFilters={updateAllFilters}

                onSearch={searchUserByText}
                searchVal={searchVal}

                searchButton={false}
                filtersSetFromOtherScreen={filtersSetFromOtherScreen}
                setFiltersSetFromOtherScreen={setFiltersSetFromOtherScreenFunc}

                tableId={userPreferencesComponentIds.USER_ACTIVE_MAP}
                noItemsMessage='There are no users to display'
                customFilterBehavior={true}
                mapExportData={mapExportData}
            />
            <EditUserDialog user={clickedUser} refetchUsers={refetchUsers} clients={clients} lots={lots} roles={roles} onCancel={onCancel} />
        </>
    );
};

export default memo(UsersTable);
